import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import {
    Row,
    Col,
    Card,
} from "react-bootstrap";

import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { Skeleton } from "primereact/skeleton";
import { Dialog } from "primereact/dialog";

import { GiTwoCoins } from "react-icons/gi";
import { BsGift } from "react-icons/bs";
import {
    FaTrophy,
    FaCalendarAlt,
    FaLock,
} from "react-icons/fa";

import { MdOutlineAccountBalanceWallet } from "react-icons/md";

import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useAuth } from "../../context/AuthContext";
import api from "../../api/axios";

import "../../assets/css/Useraccount.css";
import "../../assets/css/CustomerSpinWheel.css";


/* ============================================================
   TIER NAMES
   ============================================================ */

const TIER_NAMES = {
    1: "Starter",
    2: "Premium",
    3: "Premium Plus",
    4: "Pro",
};


/* ============================================================
   WHEEL COLOURS

   These repeat automatically if more prizes are added.
   ============================================================ */

const WHEEL_COLORS = [
    "#FFADD8",
    "#F7E3C2",
    "#E98BBB",
    "#E7BF73",
    "#F4CFDF",
    "#D9A94E",
    "#FF8FC9",
    "#F3D8A4",
];


/* ============================================================
   COMPONENT
   ============================================================ */

const CustomerSpinWheel = () => {

    const navigate = useNavigate();

    const {
        isAuthenticated,
    } = useAuth();


    /* ========================================================
       API DATA
       ======================================================== */

    const [
        spinData,
        setSpinData,
    ] = useState(null);


    const [
        loading,
        setLoading,
    ] = useState(true);


    /* ========================================================
       SPIN STATE
       ======================================================== */

    const [
        spinning,
        setSpinning,
    ] = useState(false);


    /*
     * Total rotation in degrees.
     *
     * We keep increasing this instead of resetting to zero
     * so every spin keeps travelling forwards.
     */

    const [
        rotation,
        setRotation,
    ] = useState(0);


    /*
     * Backend result is stored here while animation runs.
     *
     * We intentionally DO NOT display the prize immediately.
     */

    const [
        pendingResult,
        setPendingResult,
    ] = useState(null);


    const [
        latestWin,
        setLatestWin,
    ] = useState(null);


    const [
        resultDialog,
        setResultDialog,
    ] = useState(false);


    /*
     * Prevent accidental duplicate transition-complete handling.
     */

    const finishingSpin =
        useRef(false);


    /* ========================================================
       HELPERS
       ======================================================== */

    const getTierName = (tier) => {

        return (
            TIER_NAMES[
                Number(tier)
            ] ||
            `Tier ${tier}`
        );

    };


    const getTierLabel = (tier) => {

        if (
            tier === undefined ||
            tier === null
        ) {
            return "Subscription";
        }


        const tierNumber =
            Number(tier);


        return (
            `Tier ${tierNumber} · ${getTierName(
                tierNumber
            )}`
        );

    };


    const showError = (
        error,
        fallback
    ) => {

        toast.error(
            error?.response?.data?.message ||
            fallback
        );

    };


    const formatDate = (value) => {

        if (!value) {
            return "—";
        }


        const date =
            new Date(value);


        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return "—";
        }


        return date.toLocaleString(
            "en-IN",
            {
                day:
                    "2-digit",

                month:
                    "short",

                year:
                    "numeric",

                hour:
                    "2-digit",

                minute:
                    "2-digit",
            }
        );

    };


    /* ========================================================
       FETCH STATUS
       ======================================================== */

    const fetchSpinStatus =
        useCallback(async () => {

            try {

                setLoading(true);


                const response =
                    await api.get(
                        "/SpinRoutes/customer/spin/status"
                    );


                if (
                    response.data?.success
                ) {

                    setSpinData(
                        response.data
                    );

                } else {

                    setSpinData(null);

                }


            } catch (error) {

                console.error(
                    "Spin status fetch error:",
                    error
                );


                setSpinData(null);


                if (
                    error?.response?.status ===
                    401
                ) {
                    return;
                }


                showError(
                    error,
                    "Unable to load Spin the Wheel."
                );


            } finally {

                setLoading(false);

            }

        }, []);


    /* ========================================================
       INITIAL LOAD
       ======================================================== */

    useEffect(() => {

        if (!isAuthenticated) {

            setLoading(false);

            return;
        }


        fetchSpinStatus();

    }, [
        isAuthenticated,
        fetchSpinStatus,
    ]);


    /* ========================================================
       DERIVED DATA
       ======================================================== */

    const prizes =
        Array.isArray(
            spinData?.prizes
        )
            ? spinData.prizes
            : [];


    const hasActiveSubscription =
        Boolean(
            spinData
                ?.has_active_subscription
        );


    const eligible =
        Boolean(
            spinData?.eligible
        );


    const alreadySpun =
        Boolean(
            spinData?.already_spun
        );


    const subscription =
        spinData?.subscription ||
        null;


    const stats =
        spinData?.stats || {
            total_spins: 0,
            total_coins_won: 0,
            biggest_win: 0,
        };


    /* ========================================================
       BUILD WHEEL GRADIENT
       ======================================================== */

    const buildWheelGradient = () => {

        if (
            prizes.length === 0
        ) {
            return "#202020";
        }


        const segmentSize =
            100 /
            prizes.length;


        /*
         * Offset by half a segment so prize 0 is centred
         * under the top pointer.
         */

        const anglePerSegment =
            360 /
            prizes.length;


        const startAngle =
            -anglePerSegment /
            2;


        const segments =
            prizes.map(
                (
                    prize,
                    index
                ) => {

                    const start =
                        index *
                        segmentSize;

                    const end =
                        (
                            index +
                            1
                        ) *
                        segmentSize;

                    const color =
                        WHEEL_COLORS[
                            index %
                            WHEEL_COLORS.length
                        ];


                    return (
                        `${color} ${start}% ${end}%`
                    );

                }
            );


        return (
            `conic-gradient(
                from ${startAngle}deg,
                ${segments.join(",")}
            )`
        );

    };


    /* ========================================================
       SPIN
       ======================================================== */

    const handleSpin =
        async () => {

            if (spinning) {
                return;
            }


            if (
                !hasActiveSubscription
            ) {

                toast.warning(
                    "An active subscription is required to spin the wheel."
                );

                return;
            }


            if (
                alreadySpun ||
                !eligible
            ) {

                toast.info(
                    "You have already used your spin today."
                );

                return;
            }


            if (
                prizes.length === 0
            ) {

                toast.warning(
                    "The Spin Wheel is currently unavailable."
                );

                return;
            }


            try {

                /*
                 * Disable immediately so the customer cannot
                 * double click.
                 */

                setSpinning(true);

                setLatestWin(null);


                const response =
                    await api.post(
                        "/SpinRoutes/customer/spin"
                    );


                if (
                    !response.data?.success ||
                    !response.data?.data
                ) {

                    throw new Error(
                        response.data?.message ||
                        "Unable to complete spin"
                    );

                }


                const result =
                    response.data.data;


                /* =============================================
                   FIND WINNING INDEX

                   Backend already returns wheel_index.

                   Product ID lookup acts as safety fallback.
                   ============================================= */

                let winningIndex =
                    Number(
                        result.prize
                            ?.wheel_index
                    );


                if (
                    !Number.isInteger(
                        winningIndex
                    ) ||
                    winningIndex < 0 ||
                    winningIndex >=
                        prizes.length
                ) {

                    winningIndex =
                        prizes.findIndex(
                            (prize) =>
                                Number(
                                    prize
                                        .spin_prize_id
                                ) ===
                                Number(
                                    result.prize
                                        ?.spin_prize_id
                                )
                        );

                }


                if (
                    winningIndex < 0
                ) {

                    throw new Error(
                        "Winning wheel position could not be determined"
                    );

                }


                /*
                 * Store result but DON'T show it yet.
                 */

                setPendingResult(
                    result
                );


                /* =============================================
                   CALCULATE FINAL ROTATION
                   ============================================= */

                const anglePerSegment =
                    360 /
                    prizes.length;


                /*
                 * Prize 0 is centred at zero degrees.
                 *
                 * Therefore winning index N must be moved:
                 *
                 * -(N * segmentAngle)
                 */

                const winningAngle =
                    winningIndex *
                    anglePerSegment;


                const currentMod =
                    (
                        (
                            rotation %
                            360
                        ) +
                        360
                    ) %
                    360;


                /*
                 * Find additional rotation required so that:
                 *
                 * current rotation
                 * + winning segment angle
                 *
                 * ends exactly at the pointer.
                 */

                const additionalToWinner =
                    (
                        360 -
                        (
                            (
                                currentMod +
                                winningAngle
                            ) %
                            360
                        )
                    ) %
                    360;


                /*
                 * 6 full rotations makes the animation feel
                 * like a real wheel.
                 */

                const fullSpins =
                    6 *
                    360;


                const nextRotation =
                    rotation +
                    fullSpins +
                    additionalToWinner;


                /*
                 * Let React first apply the spinning class,
                 * then change transform.
                 */

                requestAnimationFrame(
                    () => {

                        requestAnimationFrame(
                            () => {

                                setRotation(
                                    nextRotation
                                );

                            }
                        );

                    }
                );


            } catch (error) {

                console.error(
                    "Spin error:",
                    error
                );


                setSpinning(false);

                setPendingResult(null);


                if (
                    error?.response?.status ===
                    409
                ) {

                    toast.info(
                        error?.response?.data
                            ?.message ||
                        "You have already used today's spin."
                    );


                    await fetchSpinStatus();

                    return;
                }


                if (
                    error?.response?.status ===
                    403
                ) {

                    toast.warning(
                        error?.response?.data
                            ?.message ||
                        "An active subscription is required."
                    );


                    await fetchSpinStatus();

                    return;
                }


                showError(
                    error,
                    "Unable to spin the wheel."
                );

            }

        };


    /* ========================================================
       ANIMATION COMPLETE

       THIS is when the customer finally sees what they won.
       ======================================================== */

    const handleWheelTransitionEnd =
        (event) => {

            if (
                event.target !==
                event.currentTarget
            ) {
                return;
            }


            if (
                !pendingResult ||
                finishingSpin.current
            ) {
                return;
            }


            finishingSpin.current =
                true;


            const result =
                pendingResult;


            /*
             * Update page data immediately.
             */

            setSpinData(
                (previous) => ({
                    ...previous,

                    eligible:
                        false,

                    already_spun:
                        true,

                    wallet_balance:
                        result.wallet
                            ?.new_balance ??
                        previous
                            ?.wallet_balance ??
                        0,

                    previous_spin: {
                        spin_id:
                            result.spin_id,

                        coins_won:
                            result.prize
                                ?.coin_amount,

                        spun_at:
                            new Date()
                                .toISOString(),
                    },

                    stats: {
                        ...previous
                            ?.stats,

                        ...result.stats,

                        last_spin_at:
                            new Date()
                                .toISOString(),
                    },

                    day: {
                        ...previous
                            ?.day,

                        next_spin_at:
                            result
                                ?.next_spin_at ||
                            previous
                                ?.day
                                ?.next_spin_at,
                    },
                })
            );


            /*
             * NOW reveal result.
             */

            setLatestWin(
                result
            );

            setPendingResult(
                null
            );

            setSpinning(
                false
            );

            setResultDialog(
                true
            );


            const coinsWon =
                Number(
                    result.prize?.coin_amount || 0
                );


            if (coinsWon > 0) {

                toast.success(
                    `You won ${coinsWon} credit coins!`
                );

            } else {

                toast.info(
                    "Better luck next time! Your next spin will be available tomorrow."
                );

            }


            /*
             * Release transition guard.
             */

            setTimeout(
                () => {

                    finishingSpin.current =
                        false;

                },
                100
            );

        };


    /* ========================================================
       BUTTON TEXT
       ======================================================== */

    const getSpinButtonLabel =
        () => {

            if (spinning) {
                return "Spinning...";
            }


            if (
                !hasActiveSubscription
            ) {
                return "Subscription Required";
            }


            if (
                alreadySpun
            ) {
                return "Spin Used";
            }


            return "Spin Now";

        };


    /* ========================================================
       SKELETON
       ======================================================== */

    const SpinSkeleton = () => {

        return (

            <Row className="g-4">

                <Col
                    lg={8}
                    xs={12}
                >

                    <Card className="
                        bg-dark
                        border-secondary
                        p-4
                    ">

                        <div className="
                            d-flex
                            justify-content-center
                        ">

                            <Skeleton
                                shape="circle"
                                size="420px"
                            />

                        </div>

                    </Card>

                </Col>


                <Col
                    lg={4}
                    xs={12}
                >

                    <Card className="
                        bg-dark
                        border-secondary
                        h-100
                    ">

                        <Card.Body>

                            <Skeleton
                                width="50%"
                                height="1.5rem"
                                className="mb-4"
                            />

                            <Skeleton
                                width="100%"
                                height="80px"
                                className="mb-3"
                            />

                            <Skeleton
                                width="100%"
                                height="80px"
                                className="mb-3"
                            />

                            <Skeleton
                                width="100%"
                                height="48px"
                            />

                        </Card.Body>

                    </Card>

                </Col>

            </Row>

        );

    };


    /* ========================================================
       NOT LOGGED IN
       ======================================================== */

    if (
        !isAuthenticated
    ) {

        return (

            <Row className="
                mx-0
                user-dash
            ">

                <Col
                    xs={12}
                    className="
                        text-center
                        py-5
                        text-white
                    "
                >

                    <BsGift
                        size={58}
                        className="
                            mb-3
                            text-white
                        "
                    />


                    <h4 className="text-white">

                        Spin & Win

                    </h4>


                    <p className="text-white">

                        Login to use your daily spin and win credit coins.

                    </p>


                    <Button
                        label="Login"
                        className="pink_button"

                        onClick={() =>
                            navigate(
                                "/login"
                            )
                        }
                    />

                </Col>

            </Row>

        );

    }


    /* ========================================================
       RENDER
       ======================================================== */

    return (

        <Row
            className="
                mx-0
                user-dash
                text-white
                customer-spin-page
            "
        >

            {/* =================================================
                HEADER
                ================================================= */}

            <Col
                xs={12}
                className="
                    prof_title
                    mb-4
                "
            >

                <div
                    className="
                        d-flex
                        justify-content-between
                        align-items-start
                        gap-3
                        flex-wrap
                    "
                >

                    <div>

                        <div className="
                            sm-spin-eyebrow
                            mb-2
                        ">

                            DAILY MEMBER REWARD

                        </div>
                    </div>


                    {/*--{!loading &&
                        subscription && (

                        <div
                            className="
                                d-flex
                                align-items-center
                                gap-2
                                flex-wrap
                            "
                        >

                            <Badge
                                value={
                                    getTierLabel(
                                        subscription
                                            .tier_level
                                    )
                                }
                                severity="warning"
                            />

                            <span className="
                                small
                                text-white
                            ">

                                {
                                    subscription
                                        .subscription_name
                                }

                            </span>

                        </div>

                    )}--*/}

                </div>

            </Col>


            {/* =================================================
                NO ACTIVE SUBSCRIPTION
                ================================================= */}

            {!loading &&
                !hasActiveSubscription && (

                <Col
                    xs={12}
                    className="mb-4"
                >

                    <Card className="
                        sm-spin-subscription-card
                        text-white
                    ">

                        <Card.Body
                            className="
                                d-flex
                                align-items-center
                                justify-content-between
                                gap-3
                                flex-wrap
                            "
                        >

                            <div className="
                                d-flex
                                align-items-center
                                gap-3
                            ">

                                <div className="
                                    sm-spin-lock-icon
                                ">

                                    <FaLock />

                                </div>


                                <div>

                                    <h5
                                        className="
                                            text-white
                                            mb-1
                                        "
                                    >

                                        Subscriber Reward

                                    </h5>


                                    <p
                                        className="
                                            text-white
                                            mb-0
                                        "
                                    >

                                        An active subscription is required to use your daily spin.

                                    </p>

                                </div>

                            </div>


                            <Button
                                label="View Subscription Plans"
                                className="pink_button"

                                onClick={() =>
                                    navigate(
                                        "/packages"
                                    )
                                }
                            />

                        </Card.Body>

                    </Card>

                </Col>

            )}


            {/* =================================================
                LOADING
                ================================================= */}

            {loading && (

                <Col xs={12}>

                    <SpinSkeleton />

                </Col>

            )}


            {/* =================================================
                MAIN CONTENT
                ================================================= */}

            {!loading &&
                prizes.length > 0 && (

                <>
                    {/* =========================================
                        WHEEL
                        ========================================= */}

                    <Col
                        xl={8}
                        lg={12}
                        xs={12}
                        className="mb-4"
                    >

                        <Card className="
                            sm-spin-wheel-card
                            h-100
                        ">

                            <Card.Body>

                                <div className="
                                    sm-spin-wheel-heading
                                    text-center
                                    mb-4
                                ">

                                    <span>
                                        YOUR DAILY SPIN
                                    </span>


                                    <h3 className="
                                        text-white
                                        mt-2
                                    ">

                                        Try Your Luck

                                    </h3>

                                </div>


                                <div className="
                                    sm-spin-wheel-stage
                                ">

                                    {/* POINTER */}

                                    <div className="
                                        sm-spin-pointer
                                    " />


                                    {/* ROTATING WHEEL */}

                                    <div
                                        className={`
                                            sm-spin-wheel
                                            ${
                                                spinning
                                                    ? "spinning"
                                                    : ""
                                            }
                                        `}

                                        style={{
                                            background:
                                                buildWheelGradient(),

                                            transform:
                                                `rotate(${rotation}deg)`,
                                        }}

                                        onTransitionEnd={
                                            handleWheelTransitionEnd
                                        }
                                    >

                                        {prizes.map(
                                            (
                                                prize,
                                                index
                                            ) => {

                                                const segmentAngle =
                                                    360 /
                                                    prizes.length;


                                                const angle =
                                                    index *
                                                    segmentAngle;


                                                /*
                                                 * Position labels around wheel.
                                                 */

                                                const radians =
                                                    (
                                                        angle *
                                                        Math.PI
                                                    ) /
                                                    180;


                                                const radius =
                                                    34;


                                                const left =
                                                    50 +
                                                    radius *
                                                    Math.sin(
                                                        radians
                                                    );


                                                const top =
                                                    50 -
                                                    radius *
                                                    Math.cos(
                                                        radians
                                                    );


                                                return (

                                                    <div
                                                        key={
                                                            prize
                                                                .spin_prize_id
                                                        }

                                                        className="
                                                            sm-spin-prize-label
                                                        "

                                                        style={{
                                                            left:
                                                                `${left}%`,

                                                            top:
                                                                `${top}%`,

                                                            transform:
                                                                `
                                                                    translate(
                                                                        -50%,
                                                                        -50%
                                                                    )
                                                                    rotate(
                                                                        ${angle}deg
                                                                    )
                                                                `,
                                                        }}
                                                    >

                                                        {Number(
                                                            prize.coin_amount
                                                        ) > 0 ? (

                                                            <>
                                                                <GiTwoCoins />

                                                                <strong>
                                                                    {
                                                                        prize.coin_amount
                                                                    }
                                                                </strong>

                                                                <small>
                                                                    COINS
                                                                </small>
                                                            </>

                                                        ) : (

                                                            <>
                                                                <strong
                                                                    style={{
                                                                        fontSize:
                                                                            "0.9em",
                                                                        lineHeight:
                                                                            1.05,
                                                                    }}
                                                                >
                                                                    BETTER
                                                                    <br />
                                                                    LUCK
                                                                </strong>

                                                                <small>
                                                                    NEXT TIME
                                                                </small>
                                                            </>

                                                        )}

                                                    </div>

                                                );

                                            }
                                        )}


                                        {/* INNER RING */}

                                        <div className="
                                            sm-spin-wheel-inner-ring
                                        " />

                                    </div>


                                    {/* NON-ROTATING CENTRE */}

                                    <div className="
                                        sm-spin-wheel-center
                                    ">

                                        <GiTwoCoins />

                                        <span>
                                            WIN
                                        </span>

                                    </div>

                                </div>


                                <div className="
                                    text-center
                                    mt-4
                                ">

                                    <Button
                                        label={
                                            getSpinButtonLabel()
                                        }

                                        icon={
                                            spinning
                                                ? "pi pi-spin pi-spinner"
                                                : alreadySpun
                                                    ? "pi pi-check"
                                                    : !hasActiveSubscription
                                                        ? "pi pi-lock"
                                                        : "pi pi-star"
                                        }

                                        className="
                                            pink_button
                                            sm-spin-main-button
                                        "

                                        disabled={
                                            spinning ||
                                            !eligible
                                        }

                                        onClick={
                                            handleSpin
                                        }
                                    />

                                </div>

                            </Card.Body>

                        </Card>

                    </Col>


                    {/* =========================================
                        RIGHT STATUS
                        ========================================= */}

                    <Col
                        xl={4}
                        lg={12}
                        xs={12}
                        className="mb-4"
                    >

                        <Card className="
                            sm-spin-status-card
                            h-100
                        ">

                            <Card.Body
                                className="
                                    d-flex
                                    flex-column
                                "
                            >

                                <div className="mb-4">

                                    <div className="
                                        sm-spin-status-label
                                    ">

                                        DAILY STATUS

                                    </div>


                                    <h4 className="
                                        text-white
                                        mt-2
                                        mb-2
                                    ">

                                        {alreadySpun
                                            ? "Spin Completed"
                                            : hasActiveSubscription
                                                ? "Your Spin Is Ready"
                                                : "Subscription Required"}

                                    </h4>


                                    <p className="
                                        text-white
                                        mb-0
                                    ">

                                        {alreadySpun
                                            ? "You have already used your spin for today."
                                            : hasActiveSubscription
                                                ? "You have one spin available. Your reward is added instantly to your wallet."
                                                : "Activate a subscription to unlock daily rewards."}

                                    </p>

                                </div>


                                {/* LAST WIN */}

                                {spinData
                                    ?.previous_spin && (

                                    <div className="
                                        sm-spin-last-win
                                        mb-3
                                    ">

                                        <small>
                                            TODAY'S RESULT
                                        </small>


                                        {Number(
                                            spinData
                                                .previous_spin
                                                .coins_won
                                        ) > 0 ? (

                                            <div className="
                                                d-flex
                                                align-items-center
                                                gap-2
                                                mt-2
                                            ">

                                                <GiTwoCoins
                                                    size={28}
                                                />

                                                <strong>
                                                    {
                                                        spinData
                                                            .previous_spin
                                                            .coins_won
                                                    }
                                                    {" "}
                                                    Coins
                                                </strong>

                                            </div>

                                        ) : (

                                            <div className="
                                                mt-2
                                                text-white
                                            ">

                                                <strong>
                                                    Better Luck Next Time
                                                </strong>

                                            </div>

                                        )}

                                    </div>

                                )}


                                {/* NEXT SPIN */}

                                <div className="
                                    sm-spin-info-row
                                ">

                                    <div className="
                                        sm-spin-info-icon
                                    ">

                                        <FaCalendarAlt />

                                    </div>


                                    <div>

                                        <small>
                                            NEXT SPIN
                                        </small>


                                        <div className="
                                            text-white
                                            mt-1
                                        ">

                                            <b>

                                                {
                                                    formatDate(
                                                        spinData
                                                            ?.day
                                                            ?.next_spin_at
                                                    )
                                                }

                                            </b>

                                        </div>

                                    </div>

                                </div>


                                {/*-- Total Coins Won */}

                                <div className="
                                    sm-spin-info-row
                                ">

                                    <div className="
                                        sm-spin-info-icon
                                    ">

                                         <FaTrophy />

                                    </div>


                                    <div>

                                        <small>
                                            Total Coins Won
                                        </small>


                                        <div className="
                                            text-white
                                            mt-1
                                        ">

                                            <b>

                                                {
                                                    Number(
                                                        stats
                                                            .total_coins_won ||
                                                        0
                                                    )
                                                }

                                            </b>

                                        </div>

                                    </div>

                                </div>


                                {/*-- Biggest Win --*/}

                                <div className="
                                    sm-spin-info-row
                                ">

                                    <div className="
                                        sm-spin-info-icon
                                    ">

                                         <GiTwoCoins />

                                    </div>


                                    <div>

                                        <small>
                                            Biggest Win
                                        </small>


                                        <div className="
                                            text-white
                                            mt-1
                                        ">

                                            <b>

                                                {
                                                    Number(
                                                        stats
                                                            .biggest_win ||
                                                        0
                                                    )
                                                }

                                            </b>

                                        </div>

                                    </div>

                                </div>


                                {/* SUBSCRIPTION */}

                                {subscription && (

                                    <div className="
                                        sm-spin-info-row
                                    ">

                                        <div className="
                                            sm-spin-info-icon
                                        ">

                                            <FaTrophy />

                                        </div>


                                        <div>

                                            <small>
                                                MEMBER PLAN
                                            </small>


                                            <div className="
                                                text-white
                                                mt-1
                                            ">

                                                <b>

                                                    {
                                                        subscription
                                                            .subscription_name
                                                    }

                                                </b>

                                            </div>


                                            <div className="
                                                sm-spin-muted
                                                mt-1
                                            ">

                                                {
                                                    getTierLabel(
                                                        subscription
                                                            .tier_level
                                                    )
                                                }

                                            </div>

                                        </div>

                                    </div>

                                )}

                                <div className="
                                    mt-auto
                                    pt-4
                                ">

                                    <div className="
                                        sm-spin-rule
                                    ">

                                        <i className="
                                            pi
                                            pi-info-circle
                                        " />

                                        <span>

                                            One spin is available per customer every day. Unused spins do not carry forward.

                                        </span>

                                    </div>

                                </div>

                            </Card.Body>

                        </Card>

                    </Col>

                </>

            )}


            {/* =================================================
                NO PRIZES
                ================================================= */}

            {!loading &&
                prizes.length === 0 && (

                <Col xs={12}>

                    <Card className="
                        bg-dark
                        border-secondary
                        text-center
                        py-5
                    ">

                        <Card.Body>

                            <BsGift
                                size={50}
                                className="
                                    text-white
                                    mb-3
                                "
                            />


                            <h5 className="text-white">

                                Spin Wheel Unavailable

                            </h5>


                            <p className="
                                text-white
                                mb-0
                            ">

                                There are currently no active Spin & Win rewards.

                            </p>

                        </Card.Body>

                    </Card>

                </Col>

            )}


            {/* =================================================
                WIN RESULT DIALOG
                ================================================= */}

            <Dialog
                visible={
                    resultDialog
                }

                onHide={() =>
                    setResultDialog(
                        false
                    )
                }

                closable={
                    true
                }

                modal

                dismissableMask={
                    true
                }

                showHeader={
                    false
                }

                className="
                    sm-spin-result-dialog
                "

                style={{
                    width:
                        "430px",

                    maxWidth:
                        "92vw",
                }}
            >

                {latestWin && (

                    <div className="
                        sm-spin-result-content
                        text-center
                    ">

                        <div className="
                            sm-spin-result-glow
                        ">


                            <div className="
                                sm-spin-result-coin
                            ">

                                <GiTwoCoins />

                            </div>

                        </div>


                        {Number(
                            latestWin?.prize?.coin_amount || 0
                        ) > 0 ? (

                            <>
                                <div className="
                                    sm-spin-result-small
                                    mt-3
                                ">
                                    CONGRATULATIONS
                                </div>

                                <h2 className="
                                    text-white
                                    mt-2
                                    mb-1
                                ">
                                    You Won!
                                </h2>

                                <div className="
                                    sm-spin-result-value
                                ">
                                    {
                                        latestWin
                                            .prize
                                            .coin_amount
                                    }
                                </div>

                                <div className="
                                    sm-spin-result-unit
                                ">
                                    Credit Coins
                                </div>

                                <p className="
                                    text-white
                                    mt-3
                                ">
                                    Your reward has already been added to your wallet.
                                </p>
                            </>

                        ) : (

                            <>
                                <div className="
                                    sm-spin-result-small
                                    mt-3
                                ">
                                    KEEP TRYING
                                </div>

                                <h2 className="
                                    text-white
                                    mt-2
                                    mb-2
                                ">
                                    Better Luck Next Time!
                                </h2>

                                <p className="
                                    text-white
                                    mt-3
                                    mb-0
                                ">
                                    No coins this time. Your next spin will be available tomorrow.
                                </p>
                            </>

                        )}


                        <div className="
                            sm-spin-result-balance
                        ">

                            New Balance

                            <b>

                                <GiTwoCoins />

                                {" "}

                                {
                                    latestWin
                                        .wallet
                                        .new_balance
                                }

                            </b>

                        </div>


                        <Button
                            label="Got It"
                            className="
                                pink_button
                                w-100
                                mt-4
                            "

                            onClick={() =>
                                setResultDialog(
                                    false
                                )
                            }
                        />

                    </div>

                )}

            </Dialog>

        </Row>

    );

};


export default CustomerSpinWheel;